import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  LayoutDashboard, Home, Layers, FileText, Factory, GitBranch, 
  Users, Building2, MessageSquare, Mail, Image, Settings, LogOut,
  Save, Plus, Trash2, Edit, Eye, ChevronDown, ChevronRight, Upload,
  Globe, Megaphone, Award, Phone, X, Link, FolderOpen, Sparkles, RefreshCw, Power,
  UserCheck, UserX, Clock, ShieldCheck
} from "lucide-react";
import AdminSEOAgencyAccess from "@/components/admin/AdminSEOAgencyAccess";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable Image Uploader Component
function ImageUploader({ value, onChange, placeholder = "Image URL", label = "Image" }) {
  const [isUploading, setIsUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login to upload images");
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/admin/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      onChange(imageUrl);
      toast.success("Image uploaded successfully!");
    } catch (error) {
      toast.error("Failed to upload image");
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium text-[#334155]">{label}</label>}
      
      {/* Current Image Preview */}
      {value && (
        <div className="flex items-center gap-3 p-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded">
          <img src={value} alt="Preview" className="w-16 h-12 object-cover border" loading="lazy" decoding="async" onError={(e) => e.target.style.display = 'none'} />
          <p className="flex-1 text-xs text-[#64748B] truncate">{value}</p>
          <Button variant="ghost" size="sm" onClick={() => onChange("")} className="text-red-500 h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Upload Options */}
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="flex-1"
        >
          <FolderOpen className="w-4 h-4 mr-2" />
          {isUploading ? "Uploading..." : "Browse & Upload"}
        </Button>
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowUrlInput(!showUrlInput)}
          className="flex-1"
        >
          <Link className="w-4 h-4 mr-2" />
          Paste URL
        </Button>
      </div>

      {/* URL Input (toggleable) */}
      {showUrlInput && (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="text-sm"
        />
      )}
    </div>
  );
}

// Compact Image Uploader for smaller cards (like logos)
function CompactImageUploader({ value, onChange }) {
  const [isUploading, setIsUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login to upload");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5MB allowed");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/admin/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      onChange(imageUrl);
      toast.success("Uploaded!");
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-1">
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="flex-1 text-[10px] bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-1.5 hover:bg-[#F1F5F9] flex items-center justify-center gap-1"
        >
          <FolderOpen className="w-3 h-3" />
          {isUploading ? "..." : "Upload"}
        </button>
        <button
          type="button"
          onClick={() => setShowUrl(!showUrl)}
          className="flex-1 text-[10px] bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-1.5 hover:bg-[#F1F5F9] flex items-center justify-center gap-1"
        >
          <Link className="w-3 h-3" />
          URL
        </button>
      </div>
      {showUrl && (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Logo URL"
          className="text-[10px] h-7"
        />
      )}
    </div>
  );
}

// Sidebar menu structure
const menuItems = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "homepage", label: "Homepage", icon: Home, 
    subItems: [
      { id: "hero", label: "Hero Section" },
      { id: "trustMetrics", label: "Trust Metrics" },
      { id: "industrySolutions", label: "Industry Solutions" },
      { id: "featuredCaseStudies", label: "Featured Case Studies" },
      { id: "whyChooseUs", label: "Why Choose Us" },
      { id: "exhibitions", label: "Exhibitions" },
      { id: "testimonials", label: "Testimonials" },
      { id: "clientLogos", label: "Client Logos" },
    ]
  },
  { id: "solutions", label: "Solutions Pages", icon: Layers },
  { id: "caseStudies", label: "Case Studies", icon: FileText },
  { id: "manufacturing", label: "Manufacturing", icon: Factory },
  { id: "process", label: "Process", icon: GitBranch },
  { id: "partners", label: "Partners", icon: Users },
  { id: "distributors", label: "Distributors", icon: Building2 },
  { id: "blog", label: "Blog", icon: MessageSquare },
  { id: "autoBlog", label: "Auto Blog Generator", icon: Sparkles },
  { id: "contact", label: "Contact", icon: Phone },
  { id: "enquiries", label: "Enquiries", icon: Mail },
  { id: "registrations", label: "Pending Registrations", icon: UserCheck },
  { id: "media", label: "Media Library", icon: Image },
  { id: "seoAgency", label: "SEO Agency Access", icon: ShieldCheck },
  { id: "settings", label: "Site Settings", icon: Settings },
];

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState("dashboard");
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [content, setContent] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [autoBlogStats, setAutoBlogStats] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const navigate = useNavigate();

  // Handle browser back/forward with hash-based navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '') || 'dashboard';
      setActiveSection(hash);
    };

    // Set initial hash
    if (!window.location.hash) {
      window.location.hash = 'dashboard';
    } else {
      handleHashChange();
    }

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Update hash when section changes
  const handleSectionChange = (sectionId) => {
    window.location.hash = sectionId;
    setActiveSection(sectionId);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");
    
    if (!token || !userData) {
      navigate("/portal/login");
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    if (parsedUser.user_type !== "admin") {
      toast.error("Admin access required");
      navigate("/portal/login");
      return;
    }
    
    setUser(parsedUser);
    fetchAllContent(token);
    fetchEnquiries(token);
    fetchPendingRegistrations(token);
  }, [navigate]);

  const fetchAllContent = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const savedContent = response.data || {};
      const defaultContent = getDefaultContent();
      
      // Merge default content with saved content (saved takes priority)
      const mergedContent = {};
      Object.keys(defaultContent).forEach(key => {
        mergedContent[key] = savedContent[key] || defaultContent[key];
      });
      
      setContent(mergedContent);
    } catch (error) {
      setContent(getDefaultContent());
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEnquiries = async (token) => {
    try {
      const response = await axios.get(`${API}/enquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnquiries(response.data);
    } catch (error) {
      console.log("Failed to fetch enquiries");
    }
  };

  const fetchPendingRegistrations = async (tkn) => {
    try {
      const token = tkn || localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/pending-registrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingRegistrations(response.data);
    } catch (error) {
      console.log("Failed to fetch pending registrations");
    }
  };

  const approveRegistration = async (userId, userName) => {
    const token = localStorage.getItem("token");
    try {
      const res = await axios.post(`${API}/admin/approve-registration/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      if (res.data.email_sent) {
        toast.success("Welcome email sent successfully!");
      }
      fetchPendingRegistrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const rejectRegistration = async (userId, userName) => {
    if (!window.confirm(`Reject and delete registration for ${userName}?`)) return;
    const token = localStorage.getItem("token");
    try {
      const res = await axios.post(`${API}/admin/reject-registration/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchPendingRegistrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    }
  };

  const saveContent = async (section, data) => {
    const token = localStorage.getItem("token");
    setIsSaving(true);
    
    try {
      await axios.put(`${API}/admin/content/${section}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(prev => ({ ...prev, [section]: data }));
      toast.success("Content saved successfully!");
    } catch (error) {
      toast.error("Failed to save content");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/portal/login");
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateArrayItem = (section, index, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        items: prev[section].items.map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const addArrayItem = (section, template) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        items: [...(prev[section]?.items || []), template]
      }
    }));
  };

  const removeArrayItem = (section, index) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        items: prev[section].items.filter((_, i) => i !== index)
      }
    }));
  };

  // Auto Blog Generator Functions
  const loadAutoBlogStats = async () => {
    setIsLoadingStats(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/blogs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoBlogStats(response.data);
    } catch (error) {
      console.error("Failed to load auto blog stats:", error);
      toast.error("Failed to load auto blog statistics");
    } finally {
      setIsLoadingStats(false);
    }
  };

  const generateBlog = async () => {
    setIsGenerating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/admin/blogs/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Blog generated: ${response.data.blog.title}`);
      loadAutoBlogStats();
    } catch (error) {
      console.error("Failed to generate blog:", error);
      toast.error(error.response?.data?.detail || "Failed to generate blog");
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleAutoGeneration = async (enabled) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/blogs/schedule?enabled=${enabled}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Auto blog generation ${enabled ? 'enabled' : 'disabled'}`);
      loadAutoBlogStats();
    } catch (error) {
      console.error("Failed to update schedule:", error);
      toast.error("Failed to update auto generation settings");
    }
  };

  const deleteBlog = async (blogId) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Blog deleted successfully");
      loadAutoBlogStats();
    } catch (error) {
      toast.error("Failed to delete blog");
    }
  };

  useEffect(() => {
    if (activeSection === "autoBlog" && !autoBlogStats) {
      loadAutoBlogStats();
    }
  }, [activeSection, autoBlogStats]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F172A]">
        <div className="animate-spin w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full" />
      </div>
    );
  }

  const renderDashboard = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0F172A]">Dashboard Overview</h2>
      
      <div className="grid md:grid-cols-4 gap-6">
        <div className="bg-white p-6 border border-[#E2E8F0]">
          <Mail className="w-8 h-8 text-[#F97316] mb-2" />
          <div className="text-3xl font-bold text-[#0F172A]">{enquiries.length}</div>
          <div className="text-sm text-[#64748B]">Total Enquiries</div>
        </div>
        <div className="bg-white p-6 border border-[#E2E8F0]">
          <FileText className="w-8 h-8 text-blue-500 mb-2" />
          <div className="text-3xl font-bold text-[#0F172A]">{content.caseStudies?.items?.length || 5}</div>
          <div className="text-sm text-[#64748B]">Case Studies</div>
        </div>
        <div className="bg-white p-6 border border-[#E2E8F0]">
          <MessageSquare className="w-8 h-8 text-green-500 mb-2" />
          <div className="text-3xl font-bold text-[#0F172A]">{content.blog?.items?.length || 5}</div>
          <div className="text-sm text-[#64748B]">Blog Posts</div>
        </div>
        <div className="bg-white p-6 border border-[#E2E8F0]">
          <Layers className="w-8 h-8 text-purple-500 mb-2" />
          <div className="text-3xl font-bold text-[#0F172A]">13</div>
          <div className="text-sm text-[#64748B]">Industry Solutions</div>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6">
        <h3 className="text-lg font-bold text-[#0F172A] mb-4">Recent Enquiries</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B]">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B]">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B]">Industry</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B]">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {enquiries.slice(0, 5).map((enq, i) => (
                <tr key={i}>
                  <td className="px-4 py-3 text-sm">{enq.company_name}</td>
                  <td className="px-4 py-3 text-sm">{enq.contact_person}</td>
                  <td className="px-4 py-3 text-sm">{enq.industry}</td>
                  <td className="px-4 py-3 text-sm text-[#64748B]">
                    {new Date(enq.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderHeroEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Hero Section</h2>
        <Button onClick={() => saveContent('hero', content.hero)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Live Preview */}
      <div className="bg-[#0F172A] p-8 relative overflow-hidden">
        {content.hero?.backgroundImage && (
          <div className="absolute inset-0 opacity-30">
            <img src={content.hero.backgroundImage} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
        )}
        <div className="relative z-10 max-w-2xl">
          <span className="inline-block text-xs text-[#F97316] bg-white/10 px-3 py-1 mb-4">
            {content.hero?.badge || "Badge Text"}
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
            {content.hero?.headline || "Main Headline"}
          </h3>
          <p className="text-sm text-[#94A3B8] mb-4">
            {content.hero?.subheadline || "Subheadline text"}
          </p>
          <div className="flex gap-3">
            <span className="bg-[#F97316] text-white text-xs px-4 py-2 font-semibold">
              {content.hero?.ctaText || "CTA Button"}
            </span>
            <span className="bg-white/10 border border-white/30 text-white text-xs px-4 py-2">
              {content.hero?.ctaSecondary || "Secondary CTA"}
            </span>
          </div>
        </div>
        <p className="text-[10px] text-white/50 mt-4 relative z-10">↑ Live Preview</p>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-semibold text-[#0F172A] border-b pb-2">Edit Content</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Badge Text</label>
            <Input
              value={content.hero?.badge || ""}
              onChange={(e) => updateField('hero', 'badge', e.target.value)}
              placeholder="e.g., Since 2014 • ISO 9001:2015 Certified"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Primary CTA Button</label>
            <Input
              value={content.hero?.ctaText || ""}
              onChange={(e) => updateField('hero', 'ctaText', e.target.value)}
              placeholder="e.g., View Our Projects"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Main Headline</label>
          <Textarea
            value={content.hero?.headline || ""}
            onChange={(e) => updateField('hero', 'headline', e.target.value)}
            placeholder="Main hero headline"
            rows={2}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Subheadline</label>
          <Textarea
            value={content.hero?.subheadline || ""}
            onChange={(e) => updateField('hero', 'subheadline', e.target.value)}
            placeholder="Supporting text below headline"
            rows={2}
          />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Secondary CTA Button</label>
            <Input
              value={content.hero?.ctaSecondary || ""}
              onChange={(e) => updateField('hero', 'ctaSecondary', e.target.value)}
              placeholder="e.g., Request Consultation"
            />
          </div>
          <ImageUploader
            value={content.hero?.backgroundImage || ""}
            onChange={(url) => updateField('hero', 'backgroundImage', url)}
            label="Background Image"
            placeholder="https://images.unsplash.com/..."
          />
        </div>
      </div>
    </div>
  );

  const renderTrustMetricsEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Trust Metrics</h2>
        <Button onClick={() => saveContent('trustMetrics', content.trustMetrics)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Live Preview */}
      <div className="bg-[#F8FAFC] p-6">
        <p className="text-xs text-[#F97316] uppercase tracking-wider mb-2">{content.trustMetrics?.sectionSubtitle || "Section Subtitle"}</p>
        <p className="text-lg font-bold text-[#0F172A] mb-4">{content.trustMetrics?.sectionTitle || "Section Title"}</p>
        <div className="grid grid-cols-5 gap-3">
          {content.trustMetrics?.items?.map((metric, i) => (
            <div key={i} className="bg-white border p-3 text-center">
              <div className="text-xl font-bold text-[#0F172A]">{metric.number}{metric.suffix}</div>
              <div className="text-xs text-[#334155]">{metric.label}</div>
              <div className="text-[10px] text-[#64748B]">{metric.description}</div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[#64748B] mt-2">↑ Live Preview</p>
      </div>

      {/* Section Settings */}
      <div className="bg-white border border-[#E2E8F0] p-4">
        <h3 className="font-semibold text-[#0F172A] mb-4">Section Settings</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1">Section Title</label>
            <Input
              value={content.trustMetrics?.sectionTitle || ""}
              onChange={(e) => updateField('trustMetrics', 'sectionTitle', e.target.value)}
              placeholder="Numbers That Speak"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1">Section Subtitle</label>
            <Input
              value={content.trustMetrics?.sectionSubtitle || ""}
              onChange={(e) => updateField('trustMetrics', 'sectionSubtitle', e.target.value)}
              placeholder="Our Track Record"
            />
          </div>
        </div>
      </div>

      {/* Metrics Items */}
      <div className="space-y-4">
        <h3 className="font-semibold text-[#0F172A]">Metrics Items</h3>
        {content.trustMetrics?.items?.map((metric, index) => (
          <div key={index} className="bg-white border border-[#E2E8F0] p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="bg-[#0F172A] text-[#F97316] w-8 h-8 flex items-center justify-center text-sm font-bold">{index + 1}</span>
                <span className="font-medium">{metric.label || `Metric ${index + 1}`}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeArrayItem('trustMetrics', index)} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Number</label>
                <Input
                  value={metric.number || ""}
                  onChange={(e) => updateArrayItem('trustMetrics', index, 'number', e.target.value)}
                  placeholder="26"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Suffix</label>
                <Input
                  value={metric.suffix || ""}
                  onChange={(e) => updateArrayItem('trustMetrics', index, 'suffix', e.target.value)}
                  placeholder="+"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Label</label>
                <Input
                  value={metric.label || ""}
                  onChange={(e) => updateArrayItem('trustMetrics', index, 'label', e.target.value)}
                  placeholder="Years in Business"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Description</label>
                <Input
                  value={metric.description || ""}
                  onChange={(e) => updateArrayItem('trustMetrics', index, 'description', e.target.value)}
                  placeholder="Established 2014"
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => addArrayItem('trustMetrics', { number: "", label: "", suffix: "+", description: "" })}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Metric
        </Button>
      </div>
    </div>
  );

  const renderIndustrySolutionsEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Industry Solutions (Homepage)</h2>
        <Button onClick={() => saveContent('industrySolutions', content.industrySolutions)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Live Preview Grid */}
      <div className="bg-[#F8FAFC] p-4">
        <p className="text-xs text-[#F97316] uppercase mb-1">{content.industrySolutions?.sectionSubtitle || "Industry Solutions"}</p>
        <p className="text-lg font-bold text-[#0F172A] mb-3">{content.industrySolutions?.sectionTitle || "Section Title"}</p>
        <div className="grid grid-cols-4 gap-2">
          {content.industrySolutions?.items?.slice(0, 4).map((sol, i) => (
            <div key={i} className="relative h-32 bg-[#0F172A] overflow-hidden group">
              {sol.image && <img src={sol.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" loading="lazy" decoding="async" />}
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-xs font-bold text-white">{sol.title}</p>
                <p className="text-[9px] text-white/70 line-clamp-1">{sol.description}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[#64748B] mt-2">↑ Live Preview (showing first 4)</p>
      </div>

      {/* Section Settings */}
      <div className="bg-white border border-[#E2E8F0] p-4">
        <h3 className="font-semibold text-[#0F172A] mb-4">Section Settings</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1">Section Title</label>
            <Input
              value={content.industrySolutions?.sectionTitle || ""}
              onChange={(e) => updateField('industrySolutions', 'sectionTitle', e.target.value)}
              placeholder="Tailored Solutions for Every Industry"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1">Section Subtitle</label>
            <Input
              value={content.industrySolutions?.sectionSubtitle || ""}
              onChange={(e) => updateField('industrySolutions', 'sectionSubtitle', e.target.value)}
              placeholder="Industry Solutions"
            />
          </div>
        </div>
      </div>

      {/* Solutions Items */}
      <div className="space-y-4">
        <h3 className="font-semibold text-[#0F172A]">Industry Items ({content.industrySolutions?.items?.length || 0})</h3>
        {content.industrySolutions?.items?.map((solution, index) => (
          <div key={index} className="bg-white border border-[#E2E8F0] p-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {solution.image && <img src={solution.image} alt="" className="w-16 h-12 object-cover border" loading="lazy" decoding="async" />}
                <div>
                  <span className="font-medium text-[#0F172A]">{solution.title || `Solution #${index + 1}`}</span>
                  <p className="text-xs text-[#64748B]">/solutions/{solution.slug || "slug"}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeArrayItem('industrySolutions', index)} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Title</label>
                <Input
                  value={solution.title || ""}
                  onChange={(e) => updateArrayItem('industrySolutions', index, 'title', e.target.value)}
                  placeholder="IT & Technology"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Slug (URL path)</label>
                <Input
                  value={solution.slug || ""}
                  onChange={(e) => updateArrayItem('industrySolutions', index, 'slug', e.target.value)}
                  placeholder="it-technology"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Description</label>
                <Textarea
                  value={solution.description || ""}
                  onChange={(e) => updateArrayItem('industrySolutions', index, 'description', e.target.value)}
                  rows={2}
                />
              </div>
              <ImageUploader
                value={solution.image || ""}
                onChange={(url) => updateArrayItem('industrySolutions', index, 'image', url)}
                label="Industry Image"
                placeholder="https://images.unsplash.com/..."
              />
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Features (comma separated)</label>
                <Input
                  value={solution.features?.join(", ") || ""}
                  onChange={(e) => updateArrayItem('industrySolutions', index, 'features', e.target.value.split(", ").map(f => f.trim()))}
                  placeholder="Agile Workstations, Collaboration Zones"
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => addArrayItem('industrySolutions', { title: "", description: "", slug: "", image: "", features: [] })}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Industry Solution
        </Button>
      </div>
    </div>
  );

  const renderWhyChooseUsEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Why Choose Us</h2>
        <Button onClick={() => saveContent('whyChooseUs', content.whyChooseUs)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Section Title</label>
          <Input
            value={content.whyChooseUs?.title || ""}
            onChange={(e) => updateField('whyChooseUs', 'title', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Section Subtitle</label>
          <Input
            value={content.whyChooseUs?.subtitle || ""}
            onChange={(e) => updateField('whyChooseUs', 'subtitle', e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-4">
        {content.whyChooseUs?.items?.map((item, index) => (
          <div key={index} className="bg-white border border-[#E2E8F0] p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium">{item.title || `Feature #${index + 1}`}</span>
              <Button variant="ghost" size="sm" onClick={() => removeArrayItem('whyChooseUs', index)} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Title</label>
                <Input
                  value={item.title || ""}
                  onChange={(e) => updateArrayItem('whyChooseUs', index, 'title', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Highlight Number</label>
                <Input
                  value={item.highlight || ""}
                  onChange={(e) => updateArrayItem('whyChooseUs', index, 'highlight', e.target.value)}
                  placeholder="e.g., 26+"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Description</label>
                <Textarea
                  value={item.description || ""}
                  onChange={(e) => updateArrayItem('whyChooseUs', index, 'description', e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => addArrayItem('whyChooseUs', { title: "", description: "", highlight: "" })}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Feature
        </Button>
      </div>
    </div>
  );

  const renderExhibitionsEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Exhibitions</h2>
        <Button onClick={() => saveContent('exhibitions', content.exhibitions)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="space-y-4">
        {content.exhibitions?.items?.map((exhibition, index) => (
          <div key={index} className="bg-white border border-[#E2E8F0] p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium">{exhibition.name || `Exhibition #${index + 1}`}</span>
              <Button variant="ghost" size="sm" onClick={() => removeArrayItem('exhibitions', index)} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Name</label>
                <Input
                  value={exhibition.name || ""}
                  onChange={(e) => updateArrayItem('exhibitions', index, 'name', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Full Name</label>
                <Input
                  value={exhibition.fullName || ""}
                  onChange={(e) => updateArrayItem('exhibitions', index, 'fullName', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Location</label>
                <Input
                  value={exhibition.location || ""}
                  onChange={(e) => updateArrayItem('exhibitions', index, 'location', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Frequency</label>
                <Input
                  value={exhibition.frequency || ""}
                  onChange={(e) => updateArrayItem('exhibitions', index, 'frequency', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <ImageUploader
                  value={exhibition.image || ""}
                  onChange={(url) => updateArrayItem('exhibitions', index, 'image', url)}
                  label="Exhibition Image"
                  placeholder="https://images.unsplash.com/..."
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => addArrayItem('exhibitions', { name: "", fullName: "", location: "", frequency: "", image: "" })}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Exhibition
        </Button>
      </div>
    </div>
  );

  const renderTestimonialsEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Testimonials</h2>
        <Button onClick={() => saveContent('testimonials', content.testimonials)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-bold text-[#0F172A]">Video Testimonials</h3>
        <p className="text-sm text-[#64748B]">YouTube videos featured on the Testimonials page.</p>
        {content.testimonials?.items?.map((testimonial, index) => (
          <div key={index} className="border border-[#E2E8F0] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-[#64748B]">Video {index + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => removeArrayItem('testimonials', index)} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Title</label>
                <Input
                  value={testimonial.title || ""}
                  onChange={(e) => updateArrayItem('testimonials', index, 'title', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Company</label>
                <Input
                  value={testimonial.company || ""}
                  onChange={(e) => updateArrayItem('testimonials', index, 'company', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">YouTube Video ID</label>
                <Input
                  value={testimonial.videoId || ""}
                  onChange={(e) => updateArrayItem('testimonials', index, 'videoId', e.target.value)}
                  placeholder="e.g., _GOZx3HsFlE"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Thumbnail URL</label>
                <Input
                  value={testimonial.thumbnail || ""}
                  onChange={(e) => updateArrayItem('testimonials', index, 'thumbnail', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Description</label>
                <Textarea
                  value={testimonial.description || ""}
                  onChange={(e) => updateArrayItem('testimonials', index, 'description', e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => addArrayItem('testimonials', { title: "", company: "", videoId: "", thumbnail: "", description: "" })}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Video Testimonial
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-bold text-[#0F172A]">Written Testimonials</h3>
        <p className="text-sm text-[#64748B]">Client quotes displayed as cards on the Testimonials page.</p>
        {(content.testimonials?.writtenTestimonials || []).map((wt, index) => (
          <div key={index} className="border border-[#E2E8F0] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-[#64748B]">{wt.company || `Testimonial ${index + 1}`}</span>
              <Button variant="ghost" size="sm" onClick={() => {
                setContent(prev => ({
                  ...prev,
                  testimonials: {
                    ...prev.testimonials,
                    writtenTestimonials: prev.testimonials.writtenTestimonials.filter((_, i) => i !== index)
                  }
                }));
              }} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Company Name</label>
                <Input
                  value={wt.company || ""}
                  onChange={(e) => {
                    const arr = [...(content.testimonials?.writtenTestimonials || [])];
                    arr[index] = { ...arr[index], company: e.target.value };
                    updateField('testimonials', 'writtenTestimonials', arr);
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Author / Role</label>
                <Input
                  value={wt.author || ""}
                  onChange={(e) => {
                    const arr = [...(content.testimonials?.writtenTestimonials || [])];
                    arr[index] = { ...arr[index], author: e.target.value };
                    updateField('testimonials', 'writtenTestimonials', arr);
                  }}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Testimonial Text</label>
                <Textarea
                  value={wt.text || ""}
                  onChange={(e) => {
                    const arr = [...(content.testimonials?.writtenTestimonials || [])];
                    arr[index] = { ...arr[index], text: e.target.value };
                    updateField('testimonials', 'writtenTestimonials', arr);
                  }}
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Company Logo URL</label>
                <Input
                  value={wt.logo || ""}
                  onChange={(e) => {
                    const arr = [...(content.testimonials?.writtenTestimonials || [])];
                    arr[index] = { ...arr[index], logo: e.target.value };
                    updateField('testimonials', 'writtenTestimonials', arr);
                  }}
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => {
            setContent(prev => ({
              ...prev,
              testimonials: {
                ...prev.testimonials,
                writtenTestimonials: [...(prev.testimonials?.writtenTestimonials || []), { company: "", author: "", text: "", logo: "" }]
              }
            }));
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Written Testimonial
        </Button>
      </div>
    </div>
  );

  const renderClientLogosEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Client Logos</h2>
        <Button onClick={() => saveContent('clientLogos', content.clientLogos)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Live Preview - Logo Grid */}
      <div className="bg-[#F8FAFC] p-4">
        <p className="text-xs text-[#F97316] uppercase mb-1">{content.clientLogos?.sectionSubtitle || "Our Clientele"}</p>
        <p className="text-lg font-bold text-[#0F172A] mb-3">{content.clientLogos?.sectionTitle || "Clients We Have Served"}</p>
        <div className="grid grid-cols-8 gap-2">
          {content.clientLogos?.items?.slice(0, 16).map((logo, i) => (
            <div key={i} className="bg-white border p-2 h-12 flex items-center justify-center">
              {logo.logo ? (
                <img src={logo.logo} alt={logo.name} className="max-h-8 max-w-full object-contain grayscale hover:grayscale-0" loading="lazy" decoding="async" />
              ) : (
                <span className="text-[8px] text-[#94A3B8]">{logo.name}</span>
              )}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[#64748B] mt-2">↑ Live Preview ({content.clientLogos?.items?.length || 0} logos)</p>
      </div>

      {/* Section Settings */}
      <div className="bg-white border border-[#E2E8F0] p-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1">Section Title</label>
            <Input
              value={content.clientLogos?.sectionTitle || ""}
              onChange={(e) => updateField('clientLogos', 'sectionTitle', e.target.value)}
              placeholder="Clients We Have Served"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1">Section Subtitle</label>
            <Input
              value={content.clientLogos?.sectionSubtitle || ""}
              onChange={(e) => updateField('clientLogos', 'sectionSubtitle', e.target.value)}
              placeholder="Our Clientele"
            />
          </div>
        </div>
      </div>

      {/* Logo Items */}
      <div className="space-y-4">
        <h3 className="font-semibold text-[#0F172A]">Client Logos ({content.clientLogos?.items?.length || 0})</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {content.clientLogos?.items?.map((logo, index) => (
            <div key={index} className="bg-white border border-[#E2E8F0] p-3 relative group">
              <button 
                onClick={() => removeArrayItem('clientLogos', index)} 
                className="absolute -top-2 -right-2 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
              >
                <X className="w-3 h-3" />
              </button>
              <div className="h-16 flex items-center justify-center mb-3 bg-[#F8FAFC] border">
                {logo.logo ? (
                  <img src={logo.logo} alt={logo.name} className="max-h-12 max-w-full object-contain" loading="lazy" decoding="async" />
                ) : (
                  <Image className="w-8 h-8 text-[#94A3B8]" />
                )}
              </div>
              <Input
                value={logo.name || ""}
                onChange={(e) => updateArrayItem('clientLogos', index, 'name', e.target.value)}
                placeholder="Client Name"
                className="text-xs h-8 mb-2"
              />
              <CompactImageUploader
                value={logo.logo || ""}
                onChange={(url) => updateArrayItem('clientLogos', index, 'logo', url)}
              />
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          onClick={() => addArrayItem('clientLogos', { name: "", logo: "" })}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Client Logo
        </Button>
      </div>
    </div>
  );

  const renderCaseStudiesEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Case Studies</h2>
        <Button onClick={() => saveContent('caseStudies', content.caseStudies)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="space-y-4">
        {content.caseStudies?.items?.map((study, index) => (
          <div key={index} className="bg-white border border-[#E2E8F0] p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium">{study.title || `Case Study #${index + 1}`}</span>
              <Button variant="ghost" size="sm" onClick={() => removeArrayItem('caseStudies', index)} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Title</label>
                <Input
                  value={study.title || ""}
                  onChange={(e) => updateArrayItem('caseStudies', index, 'title', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Slug (URL)</label>
                <Input
                  value={study.slug || ""}
                  onChange={(e) => updateArrayItem('caseStudies', index, 'slug', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Industry</label>
                <Input
                  value={study.industry || ""}
                  onChange={(e) => updateArrayItem('caseStudies', index, 'industry', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Location</label>
                <Input
                  value={study.location || ""}
                  onChange={(e) => updateArrayItem('caseStudies', index, 'location', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Scope</label>
                <Input
                  value={study.scope || ""}
                  onChange={(e) => updateArrayItem('caseStudies', index, 'scope', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Workstations</label>
                <Input
                  type="number"
                  value={study.workstations || ""}
                  onChange={(e) => updateArrayItem('caseStudies', index, 'workstations', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Challenge</label>
                <Textarea
                  value={study.challenge || ""}
                  onChange={(e) => updateArrayItem('caseStudies', index, 'challenge', e.target.value)}
                  rows={3}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Solution</label>
                <Textarea
                  value={study.solution || ""}
                  onChange={(e) => updateArrayItem('caseStudies', index, 'solution', e.target.value)}
                  rows={3}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Outcome</label>
                <Textarea
                  value={study.outcome || ""}
                  onChange={(e) => updateArrayItem('caseStudies', index, 'outcome', e.target.value)}
                  rows={3}
                />
              </div>
              <div className="md:col-span-2">
                <ImageUploader
                  value={study.image || ""}
                  onChange={(url) => updateArrayItem('caseStudies', index, 'image', url)}
                  label="Case Study Image"
                  placeholder="https://images.unsplash.com/..."
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => addArrayItem('caseStudies', { 
            title: "", slug: "", industry: "", location: "", scope: "", 
            challenge: "", solution: "", outcome: "", workstations: "", image: "" 
          })}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Case Study
        </Button>
      </div>
    </div>
  );

  const renderBlogEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Blog Posts</h2>
        <Button onClick={() => saveContent('blog', content.blog)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="space-y-4">
        {content.blog?.items?.map((post, index) => (
          <div key={index} className="bg-white border border-[#E2E8F0] p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium">{post.title || `Blog Post #${index + 1}`}</span>
              <Button variant="ghost" size="sm" onClick={() => removeArrayItem('blog', index)} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Title</label>
                <Input
                  value={post.title || ""}
                  onChange={(e) => updateArrayItem('blog', index, 'title', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Slug (URL)</label>
                <Input
                  value={post.slug || ""}
                  onChange={(e) => updateArrayItem('blog', index, 'slug', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Category</label>
                <Input
                  value={post.category || ""}
                  onChange={(e) => updateArrayItem('blog', index, 'category', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Author</label>
                <Input
                  value={post.author || ""}
                  onChange={(e) => updateArrayItem('blog', index, 'author', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Excerpt</label>
                <Textarea
                  value={post.excerpt || ""}
                  onChange={(e) => updateArrayItem('blog', index, 'excerpt', e.target.value)}
                  rows={2}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Full Content</label>
                <Textarea
                  value={post.content || ""}
                  onChange={(e) => updateArrayItem('blog', index, 'content', e.target.value)}
                  rows={6}
                />
              </div>
              <div className="md:col-span-2">
                <ImageUploader
                  value={post.image || ""}
                  onChange={(url) => updateArrayItem('blog', index, 'image', url)}
                  label="Featured Image"
                  placeholder="https://images.unsplash.com/..."
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => addArrayItem('blog', { 
            title: "", slug: "", category: "", author: "EZEEFIT Team", 
            excerpt: "", content: "", image: "" 
          })}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Blog Post
        </Button>
      </div>
    </div>
  );

  const renderAutoBlogEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">Auto Blog Generator</h2>
          <p className="text-[#64748B]">AI-powered SEO blog automation system</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={loadAutoBlogStats} 
            variant="outline"
            disabled={isLoadingStats}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingStats ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={generateBlog} 
            disabled={isGenerating}
            className="bg-[#F97316]"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {isGenerating ? "Generating..." : "Generate Blog Now"}
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-[#E2E8F0] p-6 rounded-lg">
          <p className="text-[#64748B] text-sm">Total Blogs</p>
          <p className="text-3xl font-bold text-[#0F172A]">{autoBlogStats?.total_blogs || 0}</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] p-6 rounded-lg">
          <p className="text-[#64748B] text-sm">Auto Generated</p>
          <p className="text-3xl font-bold text-[#F97316]">{autoBlogStats?.auto_generated_blogs || 0}</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] p-6 rounded-lg">
          <p className="text-[#64748B] text-sm">Manual Blogs</p>
          <p className="text-3xl font-bold text-[#0F172A]">{autoBlogStats?.manual_blogs || 0}</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] p-6 rounded-lg">
          <p className="text-[#64748B] text-sm">Schedule Status</p>
          <p className={`text-xl font-bold ${autoBlogStats?.schedule?.enabled ? 'text-green-600' : 'text-red-500'}`}>
            {autoBlogStats?.schedule?.enabled ? 'Active' : 'Paused'}
          </p>
        </div>
      </div>

      {/* Schedule Control */}
      <div className="bg-white border border-[#E2E8F0] p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-[#0F172A]">Auto Generation Schedule</h3>
            <p className="text-sm text-[#64748B]">
              Generates {autoBlogStats?.schedule?.blogs_per_week || 3} blogs per week on {autoBlogStats?.schedule?.days?.join(", ") || "Mon, Wed, Fri"}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant={autoBlogStats?.schedule?.enabled ? "outline" : "default"}
              onClick={() => toggleAutoGeneration(true)}
              className={autoBlogStats?.schedule?.enabled ? "border-green-500 text-green-600" : "bg-green-600"}
            >
              <Power className="w-4 h-4 mr-2" />
              Enable
            </Button>
            <Button 
              variant={!autoBlogStats?.schedule?.enabled ? "outline" : "default"}
              onClick={() => toggleAutoGeneration(false)}
              className={!autoBlogStats?.schedule?.enabled ? "border-red-500 text-red-600" : "bg-red-600"}
            >
              <Power className="w-4 h-4 mr-2" />
              Disable
            </Button>
          </div>
        </div>
        
        <div className="bg-[#F8FAFC] p-4 rounded text-sm text-[#64748B]">
          <p className="font-medium text-[#0F172A] mb-2">How it works:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>AI generates unique SEO-optimized blogs automatically</li>
            <li>Topics include: City guides, Workstation layouts, Industry solutions, Furniture guides</li>
            <li>Each blog is 1500-2000 words with internal links</li>
            <li>Estimated cost: ~$0.05 per blog (~$0.60/month for 12 blogs)</li>
          </ul>
        </div>
      </div>

      {/* Category Distribution */}
      {autoBlogStats?.category_distribution && Object.keys(autoBlogStats.category_distribution).length > 0 && (
        <div className="bg-white border border-[#E2E8F0] p-6 rounded-lg">
          <h3 className="font-semibold text-[#0F172A] mb-4">Category Distribution</h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(autoBlogStats.category_distribution).map(([category, count]) => (
              <div key={category} className="bg-[#F8FAFC] px-4 py-2 rounded-full">
                <span className="text-[#0F172A] font-medium">{category}</span>
                <span className="ml-2 text-[#F97316] font-bold">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Auto-Generated Blogs */}
      <div className="bg-white border border-[#E2E8F0] p-6 rounded-lg">
        <h3 className="font-semibold text-[#0F172A] mb-4">Recent Auto-Generated Blogs</h3>
        {autoBlogStats?.recent_auto_blogs?.length > 0 ? (
          <div className="space-y-3">
            {autoBlogStats.recent_auto_blogs.map((blog, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded">
                <div>
                  <p className="font-medium text-[#0F172A]">{blog.title}</p>
                  <p className="text-sm text-[#64748B]">{blog.category} • {blog.date}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#64748B] text-center py-8">No auto-generated blogs yet. Click "Generate Blog Now" to create your first one!</p>
        )}
      </div>

      {/* Topic Templates Info */}
      <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] p-6 rounded-lg text-white">
        <h3 className="font-semibold mb-4">SEO Topic Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-[#F97316] font-medium">City-Based</p>
            <p className="text-[#94A3B8]">[Topic] in [City]</p>
            <p className="text-[#64748B] text-xs">Mumbai, Pune, Bangalore...</p>
          </div>
          <div>
            <p className="text-[#F97316] font-medium">Workstation Layout</p>
            <p className="text-[#94A3B8]">[Count] Workstation Office Design</p>
            <p className="text-[#64748B] text-xs">50, 100, 200, 500...</p>
          </div>
          <div>
            <p className="text-[#F97316] font-medium">Industry-Specific</p>
            <p className="text-[#94A3B8]">Office Design for [Industry]</p>
            <p className="text-[#64748B] text-xs">IT, BPO, Startups...</p>
          </div>
          <div>
            <p className="text-[#F97316] font-medium">Modular Furniture</p>
            <p className="text-[#94A3B8]">Benefits, Trends, Guides</p>
          </div>
          <div>
            <p className="text-[#F97316] font-medium">Design Trends</p>
            <p className="text-[#94A3B8]">Modern Office Design Ideas</p>
          </div>
          <div>
            <p className="text-[#F97316] font-medium">Productivity</p>
            <p className="text-[#94A3B8]">Workspace Optimization</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContactEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Contact Information</h2>
        <Button onClick={() => saveContent('contact', content.contact)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-bold text-[#0F172A]">Company Details</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Company Name</label>
            <Input
              value={content.contact?.companyName || ""}
              onChange={(e) => updateField('contact', 'companyName', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Tagline</label>
            <Input
              value={content.contact?.tagline || ""}
              onChange={(e) => updateField('contact', 'tagline', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-bold text-[#0F172A]">Addresses</h3>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Experience Centre Address</label>
          <Textarea
            value={content.contact?.experienceCentre || ""}
            onChange={(e) => updateField('contact', 'experienceCentre', e.target.value)}
            rows={2}
            placeholder="A-611-612, Crystal Plaza Premises, New Link Road, Andheri West, Mumbai 400053"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Manufacturing Office Address</label>
          <Textarea
            value={content.contact?.manufacturingOffice || ""}
            onChange={(e) => updateField('contact', 'manufacturingOffice', e.target.value)}
            rows={2}
            placeholder="Near Bhiwandi, Maharashtra"
          />
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-bold text-[#0F172A]">Contact Details</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Phone 1</label>
            <Input
              value={content.contact?.phone1 || ""}
              onChange={(e) => updateField('contact', 'phone1', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Phone 2</label>
            <Input
              value={content.contact?.phone2 || ""}
              onChange={(e) => updateField('contact', 'phone2', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Email (Sales)</label>
            <Input
              value={content.contact?.email || ""}
              onChange={(e) => updateField('contact', 'email', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">WhatsApp Number</label>
            <Input
              value={content.contact?.whatsapp || ""}
              onChange={(e) => updateField('contact', 'whatsapp', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Office Hours</label>
            <Input
              value={content.contact?.officeHours || ""}
              onChange={(e) => updateField('contact', 'officeHours', e.target.value)}
              placeholder="10:00 AM - 6:30 PM, Monday - Saturday"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Service Area</label>
            <Input
              value={content.contact?.serviceArea || ""}
              onChange={(e) => updateField('contact', 'serviceArea', e.target.value)}
              placeholder="Serving Pan India"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSiteSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Site Settings</h2>
        <Button onClick={() => saveContent('settings', content.settings)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Site Title</label>
          <Input
            value={content.settings?.siteTitle || ""}
            onChange={(e) => updateField('settings', 'siteTitle', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Site Description (SEO)</label>
          <Textarea
            value={content.settings?.siteDescription || ""}
            onChange={(e) => updateField('settings', 'siteDescription', e.target.value)}
            rows={2}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Logo URL</label>
          <Input
            value={content.settings?.logo || ""}
            onChange={(e) => updateField('settings', 'logo', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Footer Tagline</label>
          <Input
            value={content.settings?.footerTagline || ""}
            onChange={(e) => updateField('settings', 'footerTagline', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const renderEnquiriesView = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0F172A]">All Enquiries ({enquiries.length})</h2>
      
      <div className="bg-white border border-[#E2E8F0] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0F172A] text-white">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold">Industry</th>
                <th className="px-4 py-3 text-left text-xs font-semibold">Seats</th>
                <th className="px-4 py-3 text-left text-xs font-semibold">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {enquiries.map((enq, i) => (
                <tr key={i} className="hover:bg-[#F8FAFC]">
                  <td className="px-4 py-3 text-sm font-medium">{enq.company_name}</td>
                  <td className="px-4 py-3 text-sm">{enq.contact_person}</td>
                  <td className="px-4 py-3 text-sm">{enq.email}</td>
                  <td className="px-4 py-3 text-sm">{enq.phone}</td>
                  <td className="px-4 py-3 text-sm">{enq.industry}</td>
                  <td className="px-4 py-3 text-sm">{enq.seats_planned}</td>
                  <td className="px-4 py-3 text-sm text-[#64748B]">
                    {new Date(enq.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );


  // ==================== PENDING REGISTRATIONS ====================

  const renderPendingRegistrations = () => (
    <div className="space-y-6" data-testid="pending-registrations-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">Pending Registrations</h2>
          <p className="text-sm text-[#64748B]">Approve or reject new client registrations</p>
        </div>
        <button
          onClick={() => fetchPendingRegistrations()}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-[#E2E8F0] rounded hover:bg-[#F8FAFC] transition-colors"
          data-testid="refresh-registrations"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {pendingRegistrations.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] p-12 text-center">
          <UserCheck className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-[#64748B] font-medium">No pending registrations</p>
          <p className="text-sm text-[#94A3B8] mt-1">All registrations have been processed</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pendingRegistrations.map((reg) => (
            <div key={reg.id} className="bg-white border border-[#E2E8F0] p-5 hover:border-[#F97316] transition-colors" data-testid={`reg-card-${reg.id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-[#0F172A]">{reg.name}</h3>
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">Pending</span>
                  </div>
                  <p className="text-sm text-[#334155]"><strong>Company:</strong> {reg.company_name}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#64748B]">
                    <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {reg.email}</span>
                    <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {reg.phone}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {new Date(reg.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => approveRegistration(reg.id, reg.name)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded hover:bg-green-700 transition-colors"
                    data-testid={`approve-${reg.id}`}
                  >
                    <UserCheck className="w-4 h-4" /> Approve
                  </button>
                  <button
                    onClick={() => rejectRegistration(reg.id, reg.name)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-red-50 text-red-600 text-sm font-semibold rounded border border-red-200 hover:bg-red-100 transition-colors"
                    data-testid={`reject-${reg.id}`}
                  >
                    <UserX className="w-4 h-4" /> Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ==================== MEDIA LIBRARY ====================

  const fetchMedia = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API}/admin/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMediaFiles(response.data);
    } catch (error) {
      console.log("Failed to fetch media");
    }
  };

  const handleMediaUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const token = localStorage.getItem("token");
    setIsUploading(true);

    for (let file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await axios.post(`${API}/admin/media/upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success(`Uploaded: ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }

    setIsUploading(false);
    fetchMedia();
  };

  const handleMediaDelete = async (mediaId) => {
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/admin/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Media deleted");
      fetchMedia();
    } catch (error) {
      toast.error("Failed to delete media");
    }
  };

  const copyMediaUrl = (url) => {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success("URL copied to clipboard!");
  };

  const renderMediaLibrary = () => {
    if (mediaFiles.length === 0 && activeSection === "media") {
      fetchMedia();
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#0F172A]">Media Library</h2>
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleMediaUpload}
              className="hidden"
            />
            <Button disabled={isUploading} className="bg-[#F97316]">
              <Upload className="w-4 h-4 mr-2" />
              {isUploading ? "Uploading..." : "Upload Images"}
            </Button>
          </label>
        </div>

        <div className="bg-white border border-[#E2E8F0] p-6">
          <p className="text-sm text-[#64748B] mb-4">
            Upload images to use across your website. Click on an image to copy its URL.
          </p>
          
          {mediaFiles.length === 0 ? (
            <div className="text-center py-12 text-[#64748B]">
              <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No images uploaded yet</p>
              <p className="text-sm">Upload images to see them here</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {mediaFiles.map((media) => (
                <div key={media.id} className="group relative border border-[#E2E8F0] p-2">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${media.url}`}
                    alt={media.filename}
                    className="w-full h-24 object-cover cursor-pointer"
                    loading="lazy"
                    decoding="async"
                    onClick={() => copyMediaUrl(media.url)}
                  />
                  <p className="text-xs text-[#64748B] truncate mt-1">{media.filename}</p>
                  <button
                    onClick={() => handleMediaDelete(media.id)}
                    className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ==================== PARTNERS PAGE EDITOR ====================
  const renderPartnersEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Partners Page</h2>
        <Button onClick={() => saveContent('partners', content.partners)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Hero Title</label>
          <Input
            value={content.partners?.heroTitle || ""}
            onChange={(e) => updateField('partners', 'heroTitle', e.target.value)}
            placeholder="Architect & Consultant Partnership"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Hero Description</label>
          <Textarea
            value={content.partners?.heroDescription || ""}
            onChange={(e) => updateField('partners', 'heroDescription', e.target.value)}
            placeholder="We actively collaborate with architects..."
            rows={3}
          />
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-bold text-[#0F172A]">Partner Benefits</h3>
        {content.partners?.benefits?.map((benefit, index) => (
          <div key={index} className="border border-[#E2E8F0] p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium">Benefit #{index + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => {
                setContent(prev => ({
                  ...prev,
                  partners: {
                    ...prev.partners,
                    benefits: prev.partners.benefits.filter((_, i) => i !== index)
                  }
                }));
              }} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Title</label>
                <Input
                  value={benefit.title || ""}
                  onChange={(e) => {
                    const newBenefits = [...(content.partners?.benefits || [])];
                    newBenefits[index] = { ...newBenefits[index], title: e.target.value };
                    updateField('partners', 'benefits', newBenefits);
                  }}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Description</label>
                <Textarea
                  value={benefit.description || ""}
                  onChange={(e) => {
                    const newBenefits = [...(content.partners?.benefits || [])];
                    newBenefits[index] = { ...newBenefits[index], description: e.target.value };
                    updateField('partners', 'benefits', newBenefits);
                  }}
                  rows={2}
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => {
            setContent(prev => ({
              ...prev,
              partners: {
                ...prev.partners,
                benefits: [...(prev.partners?.benefits || []), { title: "", description: "" }]
              }
            }));
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Benefit
        </Button>
      </div>
    </div>
  );

  // ==================== MANUFACTURING PAGE EDITOR ====================
  const renderManufacturingEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Manufacturing Page</h2>
        <Button onClick={() => saveContent('manufacturing', content.manufacturing)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-bold text-[#0F172A]">Hero Section</h3>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Hero Title</label>
          <Input
            value={content.manufacturing?.heroTitle || ""}
            onChange={(e) => updateField('manufacturing', 'heroTitle', e.target.value)}
            placeholder="Manufacturing & Infrastructure"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Hero Description</label>
          <Textarea
            value={content.manufacturing?.heroDescription || ""}
            onChange={(e) => updateField('manufacturing', 'heroDescription', e.target.value)}
            rows={3}
          />
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-bold text-[#0F172A]">Capabilities</h3>
        <p className="text-sm text-[#64748B]">These appear as feature cards on the Manufacturing page.</p>
        {(content.manufacturing?.capabilities || []).map((capability, index) => (
          <div key={index} className="border border-[#E2E8F0] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-[#64748B]">Capability {index + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => {
                setContent(prev => ({
                  ...prev,
                  manufacturing: {
                    ...prev.manufacturing,
                    capabilities: prev.manufacturing.capabilities.filter((_, i) => i !== index)
                  }
                }));
              }} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <Input
              value={typeof capability === 'object' ? capability.title || "" : capability}
              onChange={(e) => {
                const newCapabilities = [...(content.manufacturing?.capabilities || [])];
                if (typeof newCapabilities[index] === 'object') {
                  newCapabilities[index] = { ...newCapabilities[index], title: e.target.value };
                } else {
                  newCapabilities[index] = { title: e.target.value, description: "" };
                }
                updateField('manufacturing', 'capabilities', newCapabilities);
              }}
              placeholder="Capability title"
            />
            <Textarea
              value={typeof capability === 'object' ? capability.description || "" : ""}
              onChange={(e) => {
                const newCapabilities = [...(content.manufacturing?.capabilities || [])];
                if (typeof newCapabilities[index] === 'object') {
                  newCapabilities[index] = { ...newCapabilities[index], description: e.target.value };
                } else {
                  newCapabilities[index] = { title: newCapabilities[index], description: e.target.value };
                }
                updateField('manufacturing', 'capabilities', newCapabilities);
              }}
              placeholder="Capability description"
              rows={2}
            />
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => {
            setContent(prev => ({
              ...prev,
              manufacturing: {
                ...prev.manufacturing,
                capabilities: [...(prev.manufacturing?.capabilities || []), { title: "", description: "" }]
              }
            }));
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Capability
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-bold text-[#0F172A]">Manufacturing Process Steps</h3>
        <p className="text-sm text-[#64748B]">The step-by-step production flow shown on the page.</p>
        {(content.manufacturing?.processSteps || []).map((step, index) => (
          <div key={index} className="border border-[#E2E8F0] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-[#64748B]">Step {String(index + 1).padStart(2, '0')}</span>
              <Button variant="ghost" size="sm" onClick={() => {
                setContent(prev => ({
                  ...prev,
                  manufacturing: {
                    ...prev.manufacturing,
                    processSteps: prev.manufacturing.processSteps.filter((_, i) => i !== index)
                  }
                }));
              }} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <Input
              value={step.title || ""}
              onChange={(e) => {
                const newSteps = [...(content.manufacturing?.processSteps || [])];
                newSteps[index] = { ...newSteps[index], title: e.target.value, number: String(index + 1).padStart(2, '0') };
                updateField('manufacturing', 'processSteps', newSteps);
              }}
              placeholder="Step title"
            />
            <Input
              value={step.description || ""}
              onChange={(e) => {
                const newSteps = [...(content.manufacturing?.processSteps || [])];
                newSteps[index] = { ...newSteps[index], description: e.target.value };
                updateField('manufacturing', 'processSteps', newSteps);
              }}
              placeholder="Step description"
            />
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => {
            const currentSteps = content.manufacturing?.processSteps || [];
            const nextNum = String(currentSteps.length + 1).padStart(2, '0');
            setContent(prev => ({
              ...prev,
              manufacturing: {
                ...prev.manufacturing,
                processSteps: [...currentSteps, { number: nextNum, title: "", description: "" }]
              }
            }));
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Process Step
        </Button>
      </div>
    </div>
  );

  // ==================== PROCESS PAGE EDITOR ====================
  const renderProcessEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Process Page</h2>
        <Button onClick={() => saveContent('process', content.process)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Hero Title</label>
          <Input
            value={content.process?.heroTitle || ""}
            onChange={(e) => updateField('process', 'heroTitle', e.target.value)}
            placeholder="Our Execution Process"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Hero Description</label>
          <Textarea
            value={content.process?.heroDescription || ""}
            onChange={(e) => updateField('process', 'heroDescription', e.target.value)}
            rows={3}
          />
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-bold text-[#0F172A]">Process Steps</h3>
        {content.process?.steps?.map((step, index) => (
          <div key={index} className="border border-[#E2E8F0] p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium">Step {String(index + 1).padStart(2, '0')}</span>
              <Button variant="ghost" size="sm" onClick={() => {
                setContent(prev => ({
                  ...prev,
                  process: {
                    ...prev.process,
                    steps: prev.process.steps.filter((_, i) => i !== index)
                  }
                }));
              }} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Title</label>
                <Input
                  value={step.title || ""}
                  onChange={(e) => {
                    const newSteps = [...(content.process?.steps || [])];
                    newSteps[index] = { ...newSteps[index], title: e.target.value };
                    updateField('process', 'steps', newSteps);
                  }}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Description</label>
                <Textarea
                  value={step.description || ""}
                  onChange={(e) => {
                    const newSteps = [...(content.process?.steps || [])];
                    newSteps[index] = { ...newSteps[index], description: e.target.value };
                    updateField('process', 'steps', newSteps);
                  }}
                  rows={2}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Deliverables (comma-separated)</label>
                <Input
                  value={(step.deliverables || []).join(", ")}
                  onChange={(e) => {
                    const newSteps = [...(content.process?.steps || [])];
                    newSteps[index] = { ...newSteps[index], deliverables: e.target.value.split(",").map(d => d.trim()).filter(Boolean) };
                    updateField('process', 'steps', newSteps);
                  }}
                  placeholder="e.g., Site survey report, Requirement document, Initial consultation"
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => {
            setContent(prev => ({
              ...prev,
              process: {
                ...prev.process,
                steps: [...(prev.process?.steps || []), { title: "", description: "" }]
              }
            }));
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Step
        </Button>
      </div>
    </div>
  );

  // ==================== DISTRIBUTORS PAGE EDITOR ====================
  const renderDistributorsEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Distributors Page</h2>
        <Button onClick={() => saveContent('distributors', content.distributors)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Hero Title</label>
          <Input
            value={content.distributors?.heroTitle || ""}
            onChange={(e) => updateField('distributors', 'heroTitle', e.target.value)}
            placeholder="Become a Distributor"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Hero Description</label>
          <Textarea
            value={content.distributors?.heroDescription || ""}
            onChange={(e) => updateField('distributors', 'heroDescription', e.target.value)}
            rows={3}
          />
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-bold text-[#0F172A]">Distributor Benefits</h3>
        {content.distributors?.benefits?.map((benefit, index) => (
          <div key={index} className="border border-[#E2E8F0] p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium">Benefit #{index + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => {
                setContent(prev => ({
                  ...prev,
                  distributors: {
                    ...prev.distributors,
                    benefits: prev.distributors.benefits.filter((_, i) => i !== index)
                  }
                }));
              }} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Title</label>
                <Input
                  value={benefit.title || ""}
                  onChange={(e) => {
                    const newBenefits = [...(content.distributors?.benefits || [])];
                    newBenefits[index] = { ...newBenefits[index], title: e.target.value };
                    updateField('distributors', 'benefits', newBenefits);
                  }}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Description</label>
                <Textarea
                  value={benefit.description || ""}
                  onChange={(e) => {
                    const newBenefits = [...(content.distributors?.benefits || [])];
                    newBenefits[index] = { ...newBenefits[index], description: e.target.value };
                    updateField('distributors', 'benefits', newBenefits);
                  }}
                  rows={2}
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => {
            setContent(prev => ({
              ...prev,
              distributors: {
                ...prev.distributors,
                benefits: [...(prev.distributors?.benefits || []), { title: "", description: "" }]
              }
            }));
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Benefit
        </Button>
      </div>
    </div>
  );

  // ==================== SOLUTIONS PAGE EDITOR ====================
  const renderSolutionsEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]">Solutions Page</h2>
        <Button onClick={() => saveContent('solutionsPage', content.solutionsPage)} disabled={isSaving} className="bg-[#F97316]">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Page Title</label>
          <Input
            value={content.solutionsPage?.title || ""}
            onChange={(e) => updateField('solutionsPage', 'title', e.target.value)}
            placeholder="Industry Solutions"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Page Description</label>
          <Textarea
            value={content.solutionsPage?.description || ""}
            onChange={(e) => updateField('solutionsPage', 'description', e.target.value)}
            rows={3}
          />
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
        <h3 className="font-bold text-[#0F172A]">Industries</h3>
        <p className="text-sm text-[#64748B]">
          Edit individual industry content below. Changes here will appear on the Solutions page.
        </p>
        {content.solutionsPage?.industries?.map((industry, index) => (
          <div key={index} className="border border-[#E2E8F0] p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium">{industry.name || `Industry #${index + 1}`}</span>
              <Button variant="ghost" size="sm" onClick={() => {
                setContent(prev => ({
                  ...prev,
                  solutionsPage: {
                    ...prev.solutionsPage,
                    industries: prev.solutionsPage.industries.filter((_, i) => i !== index)
                  }
                }));
              }} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Name</label>
                <Input
                  value={industry.name || ""}
                  onChange={(e) => {
                    const newIndustries = [...(content.solutionsPage?.industries || [])];
                    newIndustries[index] = { ...newIndustries[index], name: e.target.value };
                    updateField('solutionsPage', 'industries', newIndustries);
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Slug</label>
                <Input
                  value={industry.slug || ""}
                  onChange={(e) => {
                    const newIndustries = [...(content.solutionsPage?.industries || [])];
                    newIndustries[index] = { ...newIndustries[index], slug: e.target.value };
                    updateField('solutionsPage', 'industries', newIndustries);
                  }}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#334155] mb-1">Description</label>
                <Textarea
                  value={industry.description || ""}
                  onChange={(e) => {
                    const newIndustries = [...(content.solutionsPage?.industries || [])];
                    newIndustries[index] = { ...newIndustries[index], description: e.target.value };
                    updateField('solutionsPage', 'industries', newIndustries);
                  }}
                  rows={2}
                />
              </div>
              <div className="md:col-span-2">
                <ImageUploader
                  value={industry.image || ""}
                  onChange={(url) => {
                    const newIndustries = [...(content.solutionsPage?.industries || [])];
                    newIndustries[index] = { ...newIndustries[index], image: url };
                    updateField('solutionsPage', 'industries', newIndustries);
                  }}
                  label="Industry Image"
                  placeholder="https://images.unsplash.com/..."
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() => {
            setContent(prev => ({
              ...prev,
              solutionsPage: {
                ...prev.solutionsPage,
                industries: [...(prev.solutionsPage?.industries || []), { name: "", slug: "", description: "", image: "" }]
              }
            }));
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Industry
        </Button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard": return renderDashboard();
      case "hero": return renderHeroEditor();
      case "trustMetrics": return renderTrustMetricsEditor();
      case "industrySolutions": return renderIndustrySolutionsEditor();
      case "whyChooseUs": return renderWhyChooseUsEditor();
      case "exhibitions": return renderExhibitionsEditor();
      case "testimonials": return renderTestimonialsEditor();
      case "clientLogos": return renderClientLogosEditor();
      case "caseStudies": return renderCaseStudiesEditor();
      case "blog": return renderBlogEditor();
      case "autoBlog": return renderAutoBlogEditor();
      case "contact": return renderContactEditor();
      case "enquiries": return renderEnquiriesView();
      case "registrations": return renderPendingRegistrations();
      case "settings": return renderSiteSettings();
      case "media": return renderMediaLibrary();
      case "seoAgency": return <AdminSEOAgencyAccess />;
      case "partners": return renderPartnersEditor();
      case "manufacturing": return renderManufacturingEditor();
      case "process": return renderProcessEditor();
      case "distributors": return renderDistributorsEditor();
      case "solutions": return renderSolutionsEditor();
      default: return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-[#F8FAFC]" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-[#0F172A] h-[calc(100vh-80px)] fixed left-0 top-20 flex flex-col" data-testid="admin-sidebar">
          <div className="p-4 border-b border-white/10 shrink-0">
            <h2 className="text-white font-bold">Admin Dashboard</h2>
            <p className="text-[#94A3B8] text-sm">{user?.name}</p>
          </div>

          <nav className="p-2 flex-1 overflow-y-auto" data-testid="admin-sidebar-nav">
            {menuItems.map((item) => (
              <div key={item.id}>
                <button
                  onClick={() => {
                    if (item.subItems) {
                      setExpandedMenu(expandedMenu === item.id ? null : item.id);
                    } else {
                      handleSectionChange(item.id);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 text-sm rounded transition-colors ${
                    activeSection === item.id || expandedMenu === item.id
                      ? "bg-[#F97316] text-white"
                      : "text-[#94A3B8] hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-4 h-4" />
                    {item.label}
                    {item.id === "registrations" && pendingRegistrations.length > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{pendingRegistrations.length}</span>
                    )}
                  </div>
                  {item.subItems && (
                    expandedMenu === item.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {item.subItems && expandedMenu === item.id && (
                  <div className="ml-4 mt-1 space-y-1">
                    {item.subItems.map((subItem) => (
                      <button
                        key={subItem.id}
                        onClick={() => handleSectionChange(subItem.id)}
                        className={`w-full text-left px-4 py-2 text-sm rounded transition-colors ${
                          activeSection === subItem.id
                            ? "bg-white/10 text-white"
                            : "text-[#94A3B8] hover:bg-white/5 hover:text-white"
                        }`}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="p-4 border-t border-white/10 shrink-0">
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full text-[#94A3B8] hover:text-white hover:bg-white/5"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="ml-64 flex-1 p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

function getDefaultContent() {
  return {
    hero: {
      badge: "Since 2014 • ISO 9001:2015 Certified",
      headline: "Delivering Scalable Modular Office Furniture Solutions For Growing Corporates",
      subheadline: "From concept to installation — complete workspace furniture execution under one roof. Manufacturing modular office furniture in Mumbai since 2014.",
      ctaText: "View Our Corporate Projects",
      ctaSecondary: "Request Project Consultation",
      backgroundImage: "https://images.unsplash.com/photo-1497215842964-222b430dc094?auto=format&fit=crop&w=2000&q=80"
    },
    trustMetrics: {
      sectionTitle: "Numbers That Speak",
      sectionSubtitle: "Our Track Record",
      items: [
        { number: "26", label: "Years in Business", suffix: "+", description: "Established 2014" },
        { number: "50000", label: "Workstations Delivered", suffix: "+", description: "Across industries" },
        { number: "500", label: "Corporate Clients", suffix: "+", description: "Trusted partners" },
        { number: "50", label: "Cities Executed", suffix: "+", description: "Pan-India presence" },
        { number: "100", label: "Installation Team", suffix: "+", description: "Skilled professionals" }
      ]
    },
    industrySolutions: {
      sectionTitle: "Tailored Solutions for Every Industry",
      sectionSubtitle: "Industry Solutions",
      items: [
        { 
          title: "IT & Technology", 
          slug: "it-technology",
          description: "Agile workstation setups, collaboration areas, and expansion-friendly layouts for tech companies.",
          image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1280&q=70",
          features: ["Agile Workstations", "Collaboration Zones", "Scalable Layouts"]
        },
        { 
          title: "Corporate HQ", 
          slug: "corporate-hq",
          description: "Leadership cabins, premium conference setups, and executive furniture for headquarters.",
          image: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=1280&q=70",
          features: ["Executive Cabins", "Boardroom Setup", "Premium Finishes"]
        },
        { 
          title: "Co-working Spaces", 
          slug: "coworking",
          description: "High-density seating, flexible configurations, and durable furniture for shared workspaces.",
          image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1280&q=70",
          features: ["Hot Desking", "Phone Booths", "Flexible Furniture"]
        },
        { 
          title: "BPO / Call Centers", 
          slug: "bpo",
          description: "Acoustic solutions, comfortable seating, and high-usage durable furniture for 24/7 operations.",
          image: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1280&q=70",
          features: ["Acoustic Panels", "24/7 Rated Chairs", "Cable Management"]
        }
      ]
    },
    whyChooseUs: {
      sectionTitle: "Why Corporates Choose Ezeefit",
      sectionSubtitle: "Why EZEEFIT",
      description: "We don't just supply furniture — we execute complete workspace transformations with precision and reliability.",
      items: [
        { title: "Single Point of Accountability for Office Furniture Projects", description: "From design to manufacturing to installation — we take complete ownership of your modular office furniture project. No multiple vendors. No coordination gaps." },
        { title: "Predictable Timelines in Fast-Moving Business Environments", description: "Traditional on-site carpentry leads to project delays, site mess, and workflow disruption. We offer faster installation, clean organized execution, and minimal on-site disruption." },
        { title: "Standardized Quality from a Reliable Office Furniture Manufacturer", description: "Controlled manufacturing processes, uniform finishes across workstations, cabins, and partitions, and durable, long-lasting materials. Consistent quality across every office setup." },
        { title: "Scalable Modular Office Furniture for Growing Businesses", description: "Whether you're setting up a new office, expanding your team, or reconfiguring your workspace — our modular office furniture adapts easily without rework, delays, or wastage." },
        { title: "Reduced Coordination Burden for Procurement & Admin Teams", description: "Acting as a single execution partner, providing clear timelines and communication, and managing complete site coordination. Giving your procurement and admin teams complete peace of mind." },
        { title: "Professional Office Execution That Matches Corporate Standards", description: "Clean, modern, and functional workspace designs. Professional installation processes. A workplace aligned with your corporate image." }
      ]
    },
    exhibitions: {
      sectionTitle: "Exhibitions Participated",
      sectionSubtitle: "Industry Presence",
      description: "We showcase our modular furniture systems at architecture and design exhibitions, connecting with architects, designers, and corporate decision-makers.",
      items: [
        { 
          name: "ACETECH", 
          fullName: "Architecture, Construction & Engineering Technology Expo", 
          description: "India's largest architecture and interior design exhibition",
          location: "Mumbai, Delhi, Bangalore", 
          frequency: "Annual",
          highlight: "Live modular workstation demos & product launches",
          image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1280&q=70"
        },
        { 
          name: "INDEX", 
          fullName: "International Interior Design Exhibition", 
          description: "Premier platform for furniture & interior solutions",
          location: "Mumbai", 
          frequency: "Annual",
          highlight: "Showcasing latest innovations in office furniture",
          image: "https://images.unsplash.com/photo-1591115765373-5207764f72e7?auto=format&fit=crop&w=1280&q=70"
        }
      ]
    },
    testimonials: {
      sectionTitle: "Hear From Our Clients",
      sectionSubtitle: "Client Stories",
      description: "Watch how Ezeefit has supported corporate workspaces across India.",
      items: [
        {
          title: "Corporate Client Testimonial",
          company: "Client Success Story",
          thumbnail: "/testimonials/df.png",
          videoId: "_GOZx3HsFlE",
          description: "Hear from our satisfied corporate clients about their experience with EZEEFIT furniture solutions."
        },
        {
          title: "Client Interview",
          company: "Project Experience",
          thumbnail: "/testimonials/client-interview.jpg",
          videoId: "vh2-uvT8Fto",
          description: "Our clients share their project journey and the value EZEEFIT brought to their workspace."
        }
      ],
      writtenTestimonials: [
        { company: "Avi Designs", logo: "/testimonials/avi-design.jpg", text: "Ezeefit has been our trusted partner for multiple corporate projects. Their quality, timely delivery, and professional approach make them stand out.", author: "Design Director" },
        { company: "Time Technoplast Ltd.", logo: "/testimonials/Time-logo.jpg", text: "We've worked with Ezeefit on our corporate office expansion. The furniture quality exceeded our expectations and the team was highly professional.", author: "Facilities Manager" },
        { company: "Encompass", logo: "/testimonials/Encompass-logo.jpg", text: "Excellent execution from design to installation. Ezeefit understood our requirements perfectly and delivered within the promised timeline.", author: "Project Head" },
        { company: "ARP Infra", logo: "/testimonials/arp-infra-logo.jpg", text: "Reliable partner for all our furniture needs. Their customization capability and after-sales support are commendable.", author: "Operations Manager" }
      ]
    },
    clientLogos: {
      sectionTitle: "Clients We Have Served",
      sectionSubtitle: "Our Clientele",
      items: [
        { name: "Gillette", logo: "/clientele/gillete.png" },
        { name: "P&G", logo: "/clientele/p-g.png" },
        { name: "Awfis", logo: "/clientele/awfis.png" },
        { name: "D2H", logo: "/clientele/D2H.jpg" },
        { name: "Ion Exchange", logo: "/clientele/ION-EXCHANGE.jpg" },
        { name: "Safexpress", logo: "/clientele/safexpress-logo.png" },
        { name: "T-Series", logo: "/clientele/t-series-logo.png" },
        { name: "Ultratech Cement", logo: "/clientele/Ultratech-Cement.jpg" },
        { name: "Indian Bank", logo: "/clientele/indianb.jpg" },
        { name: "L&T", logo: "/clientele/L-T-logo.jpg" },
        { name: "HPCL", logo: "/clientele/HPCL-Hindustan-Petroleum-Corporation.jpg" },
        { name: "Mother Dairy", logo: "/clientele/mother-dairy.jpg" },
        { name: "Wizcraft", logo: "/clientele/WIZCRAFT.jpg" },
        { name: "NHAVA", logo: "/clientele/nhava.png" },
        { name: "Witty", logo: "/clientele/witty.png" },
        { name: "Blenheim", logo: "/clientele/blenheim.jpg" }
      ]
    },
    manufacturing: {
      heroTitle: "Manufacturing & Infrastructure",
      heroSubtitle: "Infrastructure",
      heroDescription: "State-of-the-art manufacturing facility",
      heroImage: "https://images.unsplash.com/photo-1720036236694-d0a231c52563?auto=format&fit=crop&w=1280&q=70",
      ctaText: "Schedule a Factory Visit",
      capabilities: [
        { title: "Efficient Project Planning", description: "End-to-end project planning from design to handover — covering material sourcing, production scheduling, and milestone tracking for seamless execution." },
        { title: "Swift Multi-City Project Handling", description: "Simultaneous execution across multiple cities with centralized project coordination, ensuring consistent quality and timelines at every location." },
        { title: "Precision Time Management", description: "Strict adherence to committed timelines through systematic production planning, real-time progress tracking, and proactive bottleneck resolution." },
        { title: "Coordinated Deliveries", description: "Synchronized dispatch and logistics management across India — ensuring the right products reach the right site at the right time, every time." },
        { title: "Collaborative On-Site Installations", description: "Professional installation teams that coordinate seamlessly with other on-site contractors, architects, and facility managers for zero-disruption setup." },
        { title: "Scalable Production Capacity", description: "Flexible manufacturing capability to handle projects of every scale — from compact 50-seater offices to large corporate campuses — without compromising quality or speed." }
      ],
      processSteps: [
        { number: "01", title: "Material Sourcing", description: "Premium raw materials from certified suppliers" },
        { number: "02", title: "Precision Cutting", description: "CNC-controlled cutting for perfect dimensions" },
        { number: "03", title: "Edge Processing", description: "Seamless edge banding for durability" },
        { number: "04", title: "Assembly", description: "Expert craftsman assembly and fitting" },
        { number: "05", title: "Quality Check", description: "Multi-point inspection before dispatch" },
        { number: "06", title: "Packaging", description: "Secure packaging for safe transportation" }
      ]
    },
    process: {
      heroTitle: "Our Execution Process",
      heroSubtitle: "How We Work",
      heroDescription: "A systematic 9-step approach that ensures on-time delivery, quality excellence, and complete transparency.",
      steps: [
        { number: "01", title: "Requirement Understanding", description: "We begin with a detailed discussion to understand your workspace needs, team size, workflow patterns, and budget parameters.", deliverables: ["Site survey report", "Requirement document", "Initial consultation"] },
        { number: "02", title: "Design Consultation", description: "Our design team creates customized layouts optimized for your space, incorporating your brand aesthetics and functional requirements.", deliverables: ["2D floor plans", "3D visualizations", "Material samples"] },
        { number: "03", title: "Budgeting", description: "We provide transparent, detailed cost estimates covering materials, manufacturing, logistics, and installation with no hidden charges.", deliverables: ["Detailed quotation", "Cost breakdown", "Payment schedule"] },
        { number: "04", title: "Shop Drawing Approvals", description: "Precise shop drawings are prepared for your review and approval, ensuring every dimension and specification is confirmed before production.", deliverables: ["Technical shop drawings", "Specification sheets", "Approval sign-off"] },
        { number: "05", title: "Manufacturing", description: "Production begins in our state-of-the-art facility with strict quality control at every stage of the manufacturing process.", deliverables: ["Progress updates", "Quality inspection reports", "Dispatch schedule"] },
        { number: "06", title: "Dispatch", description: "Finished products are carefully packed and dispatched with full tracking, ensuring safe and timely delivery to your project site.", deliverables: ["Packing list", "Dispatch tracking", "Delivery schedule"] },
        { number: "07", title: "Installation", description: "Our experienced installation team ensures seamless setup with minimal disruption to your business operations.", deliverables: ["Installation plan", "Site coordination", "Daily progress reports"] },
        { number: "08", title: "Final Handover", description: "Complete quality inspection, snag rectification, and formal handover with all documentation and warranty certificates.", deliverables: ["Completion certificate", "Warranty cards", "User manual"] },
        { number: "09", title: "After Sales Support", description: "Dedicated support team for maintenance, repairs, and any future expansion or modification requirements.", deliverables: ["24/7 support line", "Annual maintenance plans", "Spare parts availability"] }
      ]
    },
    partners: {
      heroTitle: "Architect & Consultant Partnership",
      heroSubtitle: "Partner Program",
      heroDescription: "We actively collaborate with architects and interior designers to deliver functional, customized workspace furniture solutions. Your design vision, our execution expertise.",
      heroImage: "https://images.unsplash.com/photo-1577561776790-1a20c419b1a2?auto=format&fit=crop&w=1280&q=70",
      ctaText: "Become a Partner",
      benefits: [
        { title: "Customisation Support", description: "We work closely with your design specifications, offering flexibility in materials, finishes, dimensions, and configurations to match your client's vision." },
        { title: "CAD Drawing Capability", description: "Our in-house design team provides detailed 2D/3D CAD drawings, elevations, and technical specifications for seamless project integration." },
        { title: "Technical Coordination", description: "Dedicated technical team coordinates directly with your project managers for smooth execution, site measurements, and installation planning." },
        { title: "Sample Development", description: "Rapid prototyping and sample creation to help you and your clients visualize and approve designs before full-scale production." },
        { title: "Downloadable CAD Blocks", description: "Access our library of AutoCAD blocks for workstations, cabins, and furniture layouts based on our experience centre mockups." },
        { title: "White Labelling", description: "Offer our products under your brand. We provide complete white-label solutions with your branding on products and documentation." }
      ],
      cadBlocks: [
        { name: "Linear Workstations (2-seater)", size: "120x60 cm" },
        { name: "Linear Workstations (4-seater)", size: "240x60 cm" },
        { name: "L-Shape Workstations", size: "150x150 cm" },
        { name: "Executive Cabin Layout", size: "Various" },
        { name: "Conference Table (8-seater)", size: "300x120 cm" },
        { name: "Reception Counter", size: "Various" }
      ]
    },
    distributors: {
      heroTitle: "Become a Distributor",
      heroSubtitle: "Distribution Network",
      heroDescription: "Partner with EZEEFIT and bring quality office furniture solutions to your region. We're expanding our pan-India network.",
      ctaText: "Apply Now",
      benefits: [
        { title: "Exclusive Territory", description: "Get exclusive distribution rights for your city or region with protected territory agreements." },
        { title: "High Margins", description: "Competitive margins with volume-based incentives and quarterly performance bonuses." },
        { title: "Wide Product Range", description: "Access to complete office furniture range - workstations, cabins, conference tables, seating, and more." },
        { title: "Marketing Support", description: "Co-branded marketing materials, catalogs, and digital assets for local promotion." },
        { title: "Training & Support", description: "Product training, sales support, and dedicated relationship manager for your territory." },
        { title: "Quality Assurance", description: "ISO 9001:2015 certified products with warranty support and after-sales service backing." }
      ],
      requirements: [
        "Existing furniture or interior business preferred",
        "Strong local network in corporate/commercial segment",
        "Showroom space (minimum 500 sq.ft)",
        "Dedicated sales team for B2B sales",
        "Financial capability for inventory investment",
        "Commitment to brand standards and quality service"
      ]
    },
    solutionsPage: {
      title: "Industry Solutions",
      description: "Tailored workspace furniture solutions for every industry vertical",
      industries: [
        { name: "IT & Technology", slug: "it-technology", description: "Agile workstations for tech companies", image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1280&q=70" },
        { name: "Corporate HQ", slug: "corporate-hq", description: "Executive furniture for headquarters", image: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=1280&q=70" },
        { name: "Co-working", slug: "coworking", description: "Flexible solutions for shared spaces", image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1280&q=70" },
        { name: "BPO / Call Centers", slug: "bpo", description: "24/7 rated furniture solutions", image: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1280&q=70" },
        { name: "Startups", slug: "startups", description: "Budget-friendly growth solutions", image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1280&q=70" },
        { name: "Public Sector", slug: "government", description: "Compliant solutions for public sector", image: "https://images.unsplash.com/photo-1464938050520-ef2571e88c08?auto=format&fit=crop&w=1280&q=70" },
        { name: "Banking & Finance", slug: "banking", description: "Secure and professional setups", image: "https://images.unsplash.com/photo-1560472355-536de3962603?auto=format&fit=crop&w=1280&q=70" },
        { name: "Manufacturing", slug: "manufacturing", description: "Industrial office solutions", image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1280&q=70" }
      ]
    },
    caseStudies: { 
      sectionTitle: "Featured Case Studies",
      items: [] 
    },
    blog: { 
      sectionTitle: "Knowledge Hub",
      items: [] 
    },
    contact: {
      companyName: "Ezeefit Office Systems",
      tagline: "A Division of Ezeefit Modular Furniture Pvt. Ltd.",
      experienceCentre: "A-611-612, Crystal Plaza Premises, New Link Road, Andheri West, Mumbai 400053",
      manufacturingOffice: "Near Bhiwandi, Maharashtra",
      phone1: "9820042100",
      phone2: "9820596811",
      email: "hello@ezeefitofficesystems.in",
      whatsapp: "919820042100",
      officeHours: "10:00 AM - 6:30 PM, Monday - Saturday",
      serviceArea: "Serving Pan India"
    },
    settings: {
      siteTitle: "Ezeefit Office Systems",
      siteDescription: "Delivering scalable office furniture solutions for growing corporates since 2014. From concept to installation — complete workspace furniture execution under one roof.",
      logo: "",
      footerTagline: "Delivering scalable office furniture solutions for growing corporates since 2014. From concept to installation — complete workspace execution under one roof."
    }
  };
}
